import React, { useEffect, useRef, useState } from 'react';
import { fabric } from 'fabric';
import {socket} from '../socket';
import styled from 'styled-components';

const ChatHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  color: #192947;
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  /* Scrollbar Styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fbff;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #e6ecf6;
    border-radius: 10px;
    border: 2px solid #e0e8f5;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #e6ecf6;
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 20px;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border-top: 1px solid #e0e8f5;
`;

const MessageInput = styled.textarea`
  flex: 1;
  padding: 10px;
  margin-right: 10px;
  border: 1px solid #e0e8f5;
  border-radius: 5px;
  resize: none;
  height: 40px;
  background: linear-gradient(180deg, #f9fbff 0%, #f5f8fd 100%);
  color: #192947;
  font-family: inherit;

  &::placeholder {
    color: #8f9db3;
  }

  &:focus {
    outline: none;
    border-color: #335cf4;
  }

  /* Scrollbar Styling */
  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f9fbff;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #e6ecf6;
    border-radius: 10px;
    border: 2px solid #e0e8f5;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #e6ecf6;
  }
`;

const SendButton = styled.button`
  padding: 10px 20px;
  background-color: #335cf4;
  color: #ffffff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    background-color: #335cf4;
  }

  &:disabled {
    background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
    color: #8f9db3;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #d63c3c;
  padding: 10px;
  margin: 10px 0;
  border-radius: 5px;
  background-color: rgba(255, 77, 79, 0.1);
`;

const FileUploadContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 10px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileUploadButton = styled.button`
  background: none;
  border: none;
  padding: 8px;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 24px;
    height: 24px;
    filter: brightness(0) saturate(100%) invert(9%) sepia(17%) saturate(6773%) hue-rotate(204deg) brightness(91%) contrast(87%);
    opacity: 0.7;
  }

  &:hover {
    background-color: rgba(51, 92, 244, 0.1);
    
    img {
      opacity: 1;
    }
  }
`;

const FilePreview = styled.div`
  margin-top: 10px;
  padding: 10px;
  background: linear-gradient(135deg, #ffffff 0%, #f8faff 100%);
  border-radius: 5px;
  border: 1px solid #e0e8f5;
  
  p {
    margin: 0;
    color: #192947;
  }
`;

const Canvas = () => {
  const canvasRef = useRef(null);
  const [canvas, setCanvas] = useState(null);
  const isFromServer = useRef(false);
  const [color, setColor] = useState('#000000');
  const [isTransparent, setIsTransparent] = useState(false);

  useEffect(() => {
    const newCanvas = new fabric.Canvas(canvasRef.current, {
      selection: true,
      preserveObjectStacking: true,
      freeDrawingCursor: 'crosshair',
    });
  
    newCanvas.on('mouse:wheel', function(opt) {
      var delta = opt.e.deltaY;
      var zoom = newCanvas.getZoom();
      zoom *= 0.999 ** delta;
      if (zoom > 20) zoom = 20;
      if (zoom < 0.01) zoom = 0.01;
      newCanvas.zoomToPoint({ x: opt.e.offsetX, y: opt.e.offsetY }, zoom);
      opt.e.preventDefault();
      opt.e.stopPropagation();
    });
  
    let lastPosX = null;
    let lastPosY = null;
    let isDragging = false;
  
    newCanvas.on('mouse:down', function(opt) {
      var evt = opt.e;
      if (evt.altKey === true) {
        isDragging = true;
        newCanvas.selection = false;
        lastPosX = evt.clientX;
        lastPosY = evt.clientY;
      }
    });
  
    newCanvas.on('mouse:move', function(opt) {
      if (isDragging) {
        var e = opt.e;
        var vpt = newCanvas.viewportTransform;
        vpt[4] += e.clientX - lastPosX;
        vpt[5] += e.clientY - lastPosY;
        newCanvas.requestRenderAll();
        lastPosX = e.clientX;
        lastPosY = e.clientY;
      }
    });
  
    newCanvas.on('mouse:up', function(opt) {
      newCanvas.setViewportTransform(newCanvas.viewportTransform);
      isDragging = false;
      newCanvas.selection = true;
    });
  
    setCanvas(newCanvas);
  
    socket.on('canvas-data', (data) => {
      isFromServer.current = true;
      newCanvas.loadFromJSON(data, () => {
        isFromServer.current = false;
        newCanvas.renderAll();
      });
    });
  
    return () => {
      newCanvas.dispose();
      socket.off('canvas-data');
    };
  }, []);

  useEffect(() => {
    if (canvas) {
      const updateCanvas = () => {
        if (!isFromServer.current) {
          const json = canvas.toJSON();
          socket.emit('canvas-data', json);
        }
      };

      canvas.on('object:added', updateCanvas);
      canvas.on('object:modified', updateCanvas);
      canvas.on('object:removed', updateCanvas);
      canvas.on('path:created', updateCanvas);
    }
  }, [canvas]);

  const toggleDrawing = () => {
    canvas.isDrawingMode = !canvas.isDrawingMode;
    canvas.forEachObject((object) => object.selectable = !canvas.isDrawingMode);
  };

  const addLine = () => {
    const line = new fabric.Line([50, 100, 200, 100], {
      stroke: color,
      strokeWidth: 5,
      selectable: true,
    });
    canvas.add(line);
    updateCanvas();
  };

  const addArrow = () => {
    const line = new fabric.Line([50, 100, 200, 100], {
      stroke: color,
      strokeWidth: 5,
      selectable: true,
    });

    const triangle = new fabric.Triangle({
      width: 20,
      height: 15,
      fill: color,
      left: 200,
      top: 100,
      originX: 'center',
      originY: 'center',
      angle: 90,
    });

    const group = new fabric.Group([line, triangle], {
      selectable: true,
    });

    canvas.add(group);
    updateCanvas();
  };

  const addRectangle = () => {
    const rect = new fabric.Rect({
      left: 100,
      top: 100,
      fill: isTransparent ? 'transparent' : color,
      width: 200,
      height: 100,
      selectable: true,
    });
    canvas.add(rect);
    updateCanvas();
  };

  const addCircle = () => {
    const circle = new fabric.Circle({
      radius: 50,
      fill: isTransparent ? 'transparent' : color,
      left: 100,
      top: 100,
      selectable: true,
    });
    canvas.add(circle);
    updateCanvas();
  };

  const addTriangle = () => {
    const triangle = new fabric.Triangle({
      width: 100,
      height: 100,
      fill: isTransparent ? 'transparent' : color,
      left: 150,
      top: 150,
      selectable: true,
    });
    canvas.add(triangle);
    updateCanvas();
  };

  const addText = () => {
    const text = new fabric.IText('Hello Fabric!', {
      left: 100,
      top: 100,
      fontSize: 30,
      fill: color,
      selectable: true,
    });
    canvas.add(text);
    updateCanvas();
  };

  const deleteObject = () => {
    const activeObject = canvas.getActiveObject();
    if (activeObject) {
      canvas.remove(activeObject);
      canvas.requestRenderAll();
      updateCanvas();
    }
  };

  const updateCanvas = () => {
    const json = canvas.toJSON();
    socket.emit('canvas-data', json);
  };

  const handleColorChange = (e) => {
    setColor(e.target.value);
    if (canvas.getActiveObject()) {
      canvas.getActiveObject().set({ fill: e.target.value });
      canvas.requestRenderAll();
      updateCanvas();
    }
  };

  const addImage = (e) => {
    const reader = new FileReader();
    reader.onload = function (event) {
      const imgObj = new Image();
      imgObj.src = event.target.result;
      imgObj.onload = function () {
        const image = new fabric.Image(imgObj);
        image.set({
          left: 100,
          top: 100,
          angle: 0,
          padding: 10,
          cornersize: 10,
          hasRotatingPoint: true,
        });
        canvas.add(image);
        updateCanvas();
      };
    };
    reader.readAsDataURL(e.target.files[0]);
  };

  return (
    <div>
      <button onClick={toggleDrawing}>{canvas?.isDrawingMode ? 'Stop Drawing' : 'Start Drawing'}</button>
      <button onClick={deleteObject}>Delete Selected Object</button>
      <input type="color" value={color} onChange={handleColorChange} disabled={isTransparent} />
      <label>
        <input type="checkbox" checked={isTransparent} onChange={() => setIsTransparent(!isTransparent)} />
        Transparent Fill
      </label>
      <button onClick={addLine}>Line</button>
      <button onClick={addArrow}>Arrow</button>
      <button onClick={addRectangle}>Rectangle</button>
      <button onClick={addCircle}>Circle</button>
      <button onClick={addTriangle}>Triangle</button>
      <button onClick={addText}>Text</button>
      <input type="file" onChange={addImage} accept="image/*" />
      <canvas ref={canvasRef} width={800} height={600} />
    </div>
  );
};

export default Canvas;
